import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

import { isRecord } from '@/common/utils/error'
import {
  InstagramInstantwinPretestEntryCode,
  InstagramInstantwinPretestExpirationMinutes,
  InstagramInstantwinPretestLotteryResult
} from '@/typings/api/instagram_instantwin_pretest_entry_codes.schema'

import { getInstagramInstantwinPretestEntryCode } from '../api/getInstagramInstantwinPretestEntryCode'
import { postInstagramInstantwinPretestEntryCode } from '../api/postInstagramInstantwinPretestEntryCode'
import { getInstagramDmTestButtonLabel, INSTAGRAM_DM_TEST_DEFAULT_FORM } from '../utils/instagramDmTest'

const DEFAULT_LOAD_ERROR_MESSAGE = 'データ取得に失敗しました'
const DEFAULT_ISSUE_ERROR_MESSAGE = 'テストコードの発行に失敗しました'
const CAMPAIGN_STATUS_INVALID_MESSAGE = 'キャンペーンが開始されたため、テストコードの発行ができませんでした'

const getApiErrorType = (error: unknown): string | null => {
  return isRecord(error) && typeof error.type === 'string' ? error.type : null
}

export const useInstagramInstantwinPretestStore = defineStore('InstagramInstantwinPretestStore', () => {
  const prizeId = ref<number | null>(null)
  const current = ref<InstagramInstantwinPretestEntryCode | null>(null)
  const loading = ref(false)
  const submitting = ref(false)
  const loadStatus = ref<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const issueStatus = ref<'idle' | 'submitting'>('idle')
  const disabled = ref(false)
  const errorMessage = ref<string | null>(null)
  const lastErrorType = ref<string | null>(null)
  const shouldRefreshCampaignState = ref(false)
  const form = reactive({
    lotteryResult: INSTAGRAM_DM_TEST_DEFAULT_FORM.lotteryResult as InstagramInstantwinPretestLotteryResult,
    expirationMinutes: INSTAGRAM_DM_TEST_DEFAULT_FORM.expirationMinutes as InstagramInstantwinPretestExpirationMinutes
  })

  const buttonLabel = computed(() => getInstagramDmTestButtonLabel(current.value))

  const resetForm = () => {
    form.lotteryResult = INSTAGRAM_DM_TEST_DEFAULT_FORM.lotteryResult
    form.expirationMinutes = INSTAGRAM_DM_TEST_DEFAULT_FORM.expirationMinutes
  }

  const syncFormWithCurrent = (nextCurrent: InstagramInstantwinPretestEntryCode | null) => {
    if (!nextCurrent) {
      resetForm()
      return
    }

    form.lotteryResult = nextCurrent.lotteryResult
    form.expirationMinutes = nextCurrent.expirationMinutes
  }

  const setDisabled = (nextDisabled: boolean) => {
    disabled.value = nextDisabled
  }

  const load = async (nextPrizeId: number, nextDisabled = false) => {
    prizeId.value = nextPrizeId
    disabled.value = nextDisabled
    loading.value = true
    loadStatus.value = 'loading'
    errorMessage.value = null
    lastErrorType.value = null
    shouldRefreshCampaignState.value = false

    try {
      const response = await getInstagramInstantwinPretestEntryCode(nextPrizeId)
      current.value = response
      syncFormWithCurrent(response)
      loadStatus.value = 'ready'
    } catch (error) {
      current.value = null
      resetForm()
      loadStatus.value = 'error'
      errorMessage.value = DEFAULT_LOAD_ERROR_MESSAGE
      lastErrorType.value = getApiErrorType(error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const issue = async () => {
    if (prizeId.value === null || issueStatus.value === 'submitting') {
      return null
    }

    submitting.value = true
    issueStatus.value = 'submitting'
    errorMessage.value = null
    lastErrorType.value = null
    shouldRefreshCampaignState.value = false

    try {
      await postInstagramInstantwinPretestEntryCode({
        prizeId: prizeId.value,
        lotteryResult: form.lotteryResult,
        expirationMinutes: form.expirationMinutes
      })

      const response = await getInstagramInstantwinPretestEntryCode(prizeId.value)
      current.value = response
      syncFormWithCurrent(response)
      return response
    } catch (error) {
      const errorType = getApiErrorType(error)

      lastErrorType.value = errorType

      if (errorType === 'CAMPAIGN_STATUS_INVALID') {
        errorMessage.value = CAMPAIGN_STATUS_INVALID_MESSAGE
        shouldRefreshCampaignState.value = true
      } else {
        errorMessage.value = DEFAULT_ISSUE_ERROR_MESSAGE
      }

      throw error
    } finally {
      submitting.value = false
      issueStatus.value = 'idle'
    }
  }

  const reset = () => {
    prizeId.value = null
    current.value = null
    loading.value = false
    submitting.value = false
    loadStatus.value = 'idle'
    issueStatus.value = 'idle'
    disabled.value = false
    errorMessage.value = null
    lastErrorType.value = null
    shouldRefreshCampaignState.value = false
    resetForm()
  }

  return {
    prizeId,
    current,
    loading,
    submitting,
    loadStatus,
    issueStatus,
    disabled,
    errorMessage,
    lastErrorType,
    shouldRefreshCampaignState,
    form,
    buttonLabel,
    setDisabled,
    load,
    issue,
    reset
  }
})
